import { useState } from "react"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import { transcribeAudioFile } from "../../lib/transcription"
import { flattenVttToPlainText } from "../../lib/vtt"
import { textareaCls, labelCls, btnSave, btnSecondary } from "./FormComponents"
import type { VideoStatus } from "../../types/video"

/**
 * Tab "Transcripción" del editor de video — texto plano usado solo como
 * insumo del Quiz IA de sección (Video.transcriptText), nunca mostrado a
 * estudiantes. Ver openspec/changes/video-transcript-for-quizzes.
 *
 * "Generar con IA" reusa el mismo Worker de Whisper que ya transcribe
 * tracks de audio (lib/transcription.ts): descarga el archivo YA SUBIDO del
 * propio video (vía una URL presignada de R2, `/raw-download-url`) y lo
 * transcribe en el navegador — el admin nunca tiene que volver a
 * seleccionarlo desde su equipo, ni importa si el video se subió antes de
 * que este flujo existiera (rawPath siempre se guardó desde la subida
 * original).
 */
export default function TranscriptPanel({
  videoId,
  status,
  initialText,
}: {
  videoId: string
  status: VideoStatus
  initialText: string | null
}) {
  const [text, setText] = useState(initialText ?? "")
  const [generating, setGenerating] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const canGenerate = status === "ready" && !generating

  async function handleGenerateClick() {
    if (!canGenerate) return

    setGenerating(true)
    setProgress("Descargando el video…")
    try {
      // Vía el propio dominio de la API (no una URL presignada directa a
      // R2): un `fetch()` cross-origin a R2 requeriría CORS habilitado en
      // el bucket, que hoy no lo está — este endpoint ya tiene CORS
      // configurado porque es el mismo dominio que usa el resto de la app.
      const fileRes = await api.get(`/academy/admin/videos/${videoId}/raw`, { responseType: "blob" })
      const blob = fileRes.data as Blob

      setProgress("Preparando audio…")
      const vtt = await transcribeAudioFile(blob, (statusLabel) => setProgress(statusLabel))
      setText(flattenVttToPlainText(vtt))
      toastSuccess("Transcripción generada. Revisa el texto antes de guardar.")
    } catch (err) {
      toastError(err)
    } finally {
      setGenerating(false)
      setProgress(null)
    }
  }

  async function handleSave() {
    setSaving(true)
    try {
      await api.put(`/academy/admin/videos/${videoId}`, {
        transcript_text: text.trim() ? text : null,
      })
      toastSuccess("Transcripción guardada.")
    } catch (err) {
      toastError(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Este texto solo se usa como insumo del Quiz IA de la sección — no se muestra a los estudiantes ni en el
        reproductor.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleGenerateClick}
          disabled={!canGenerate}
          className={`${btnSecondary} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {generating ? progress ?? "Generando…" : "Generar con IA"}
        </button>
        {status !== "ready" && (
          <span className="text-xs text-gray-500 dark:text-gray-400">El video debe estar en estado "Listo" para generar con IA.</span>
        )}
      </div>

      <div>
        <label className={labelCls}>Texto de la transcripción</label>
        <textarea
          disabled={generating}
          placeholder="Pega la transcripción aquí, o genérala con IA a partir del archivo de video/audio."
          value={text}
          onChange={(e) => setText(e.target.value)}
          className={`${textareaCls} min-h-[220px] resize-y disabled:opacity-60`}
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || generating}
          className={`${btnSave} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {saving ? "Guardando…" : "Guardar transcripción"}
        </button>
      </div>
    </div>
  )
}
